/**
 * WaFormattedText — Renderiza texto con formato estilo WhatsApp:
 *   *negrita*       → <strong>
 *   _cursiva_       → <em>
 *   ~tachado~       → <del>
 *   `monospace`     → <code>
 *   ```bloque```    → <pre><code>
 *   URLs            → <a> autodetectados
 *
 * Reglas de WhatsApp para que un marcador cuente como formato:
 *  - Debe haber un carácter no-espacio inmediatamente después del marcador de apertura
 *  - Debe haber un carácter no-espacio inmediatamente antes del marcador de cierre
 *  - El marcador debe estar al inicio de línea/string o precedido por espacio/puntuación
 *  - El cierre debe estar al final de línea/string o seguido de espacio/puntuación
 */

import React from 'react';
import { splitMentions } from './waGroupMentions';

export interface WaToken {
  type: 'text' | 'bold' | 'italic' | 'strike' | 'mono' | 'code' | 'link';
  content: string;
  href?: string;
}
type Token = WaToken;

const URL_REGEX = /\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+/gi;

// Para cada estilo: { marker, type, allowMultiline }
const STYLES: Array<{ marker: string; type: Token['type']; multiline: boolean }> = [
  { marker: '```', type: 'code', multiline: true },
  { marker: '*', type: 'bold', multiline: false },
  { marker: '_', type: 'italic', multiline: false },
  { marker: '~', type: 'strike', multiline: false },
  { marker: '`', type: 'mono', multiline: false },
];

function isBoundaryChar(c: string | undefined): boolean {
  if (!c) return true;
  return /[\s.,;:!?¿¡()[\]{}<>"']/.test(c);
}

/**
 * Encuentra el próximo marker válido (apertura) a partir de `start`.
 * Devuelve `{ idx, marker, type }` o null.
 */
function findNextMarker(text: string, start: number): { idx: number; marker: string; type: Token['type']; multiline: boolean } | null {
  let best: { idx: number; marker: string; type: Token['type']; multiline: boolean } | null = null;
  for (const s of STYLES) {
    let i = start;
    while (i < text.length) {
      const idx = text.indexOf(s.marker, i);
      if (idx === -1) break;
      const before = idx > 0 ? text[idx - 1] : undefined;
      const after = text[idx + s.marker.length];
      // Debe estar al inicio o tras un boundary, y el siguiente char no debe ser espacio
      if (isBoundaryChar(before) && after && !/\s/.test(after) && after !== s.marker[0]) {
        if (!best || idx < best.idx) best = { idx, marker: s.marker, type: s.type, multiline: s.multiline };
        break;
      }
      i = idx + 1;
    }
  }
  return best;
}

/**
 * Encuentra el cierre del marker iniciando en `start`.
 */
function findClosingMarker(text: string, start: number, marker: string, multiline: boolean): number {
  let i = start;
  while (i < text.length) {
    const idx = text.indexOf(marker, i);
    if (idx === -1) return -1;
    const before = text[idx - 1];
    const after = text[idx + marker.length];
    // El char previo no debe ser espacio, y el siguiente debe ser boundary
    if (before && !/\s/.test(before) && isBoundaryChar(after)) {
      // Para single-line markers, no permitir saltos de línea entre apertura y cierre
      if (!multiline && text.substring(start, idx).includes('\n')) return -1;
      return idx;
    }
    i = idx + 1;
  }
  return -1;
}

export function parseTokens(text: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const next = findNextMarker(text, i);
    if (!next) {
      tokens.push({ type: 'text', content: text.substring(i) });
      break;
    }
    if (next.idx > i) {
      tokens.push({ type: 'text', content: text.substring(i, next.idx) });
    }
    const contentStart = next.idx + next.marker.length;
    const closeIdx = findClosingMarker(text, contentStart, next.marker, next.multiline);
    if (closeIdx === -1) {
      // No cierre válido → tratar el resto como texto
      tokens.push({ type: 'text', content: text.substring(next.idx) });
      break;
    }
    const inner = text.substring(contentStart, closeIdx);
    tokens.push({ type: next.type, content: inner });
    i = closeIdx + next.marker.length;
  }
  return tokens;
}

/** Convierte texto plano a tokens, autodetectando URLs. */
function linkify(text: string): Array<{ type: 'text' | 'link'; content: string; href?: string }> {
  const out: Array<{ type: 'text' | 'link'; content: string; href?: string }> = [];
  let last = 0;
  URL_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', content: text.substring(last, m.index) });
    const url = m[0];
    const href = url.startsWith('www.') ? `https://${url}` : url;
    out.push({ type: 'link', content: url, href });
    last = m.index + url.length;
  }
  if (last < text.length) out.push({ type: 'text', content: text.substring(last) });
  return out;
}

function renderTokens(tokens: Token[], keyPrefix = ''): React.ReactNode[] {
  return tokens.flatMap((tok, i) => {
    const k = `${keyPrefix}${i}`;
    if (tok.type === 'text') {
      return linkify(tok.content).map((seg, j) => {
        if (seg.type === 'link') {
          return (
            <a key={`${k}-${j}`} href={seg.href} target="_blank" rel="noopener noreferrer"
              style={{ color: '#039be5', textDecoration: 'underline', wordBreak: 'break-all' }}>
              {seg.content}
            </a>
          );
        }
        return <React.Fragment key={`${k}-${j}`}>{seg.content}</React.Fragment>;
      });
    }
    if (tok.type === 'bold') {
      const inner = renderTokens(parseTokens(tok.content), `${k}-`);
      return <strong key={k}>{inner}</strong>;
    }
    if (tok.type === 'italic') {
      const inner = renderTokens(parseTokens(tok.content), `${k}-`);
      return <em key={k}>{inner}</em>;
    }
    if (tok.type === 'strike') {
      const inner = renderTokens(parseTokens(tok.content), `${k}-`);
      return <del key={k}>{inner}</del>;
    }
    if (tok.type === 'mono') {
      return (
        <code key={k} style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.92em', background: 'rgba(0,0,0,0.06)', padding: '0 4px', borderRadius: 3,
        }}>
          {tok.content}
        </code>
      );
    }
    if (tok.type === 'code') {
      return (
        <pre key={k} style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.92em', background: 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: 5,
          margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto',
        }}>
          <code>{tok.content}</code>
        </pre>
      );
    }
    return null;
  });
}

interface Props {
  text: string;
  style?: React.CSSProperties;
  as?: 'p' | 'span' | 'div';
  /** Mapa userpart→nombre para resolver @menciones de grupo. Si se pasa y
   *  el texto tiene `@<digits>`, se reemplaza por `@<nombre>` resaltado
   *  (estilo WhatsApp Web). Solo aplica en conversaciones de grupo. */
  mentionMap?: Map<string, string>;
}

export function WaFormattedText({ text, style, as = 'div', mentionMap }: Props) {
  const Tag = as;
  const segments = mentionMap && mentionMap.size > 0
    ? splitMentions(text, mentionMap)
    : [{ type: 'text' as const, content: text }];
  return (
    <Tag style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...style }}>
      {segments.map((seg, i) => {
        if (seg.type === 'mention') {
          return (
            <span key={`mn-${i}`} style={{ color: '#027eb5', fontWeight: 600 }}>
              {seg.content}
            </span>
          );
        }
        return (
          <React.Fragment key={`sg-${i}`}>
            {renderTokens(parseTokens(seg.content), `s${i}-`)}
          </React.Fragment>
        );
      })}
    </Tag>
  );
}
